import React, { useState } from 'react';
import {
  FileText, Package, CheckCircle, AlertTriangle, AlertCircle, ShoppingCart, Truck, ImageOff,
} from 'lucide-react';
import NotifyMeButton from '../NotifyMeButton';
import Button from './Button';
import Card from './Card';
import StatusBadge from './StatusBadge';
import { Skeleton, SkeletonText } from './Skeleton';
import { ProductHeader, PriceBlock, QuantityInput, InCartNote } from './ProductParts';
import { useOrderingEnabled } from '../../hooks/useOrderingEnabled';
import { formatCurrency } from '../../utils/currency';

/** Page-shaped placeholder shown while the part loads. */
export const ProductDetailSkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200" role="status" aria-label="Loading product details">
    <div className="px-6 py-5 border-b border-gray-200">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-4 w-80 max-w-full mt-3" />
    </div>
    <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="order-2 lg:order-1 lg:col-span-5">
        <Skeleton className="h-56 lg:h-80 w-full rounded-lg" />
      </div>
      <div className="order-1 lg:order-2 lg:col-span-7 space-y-6">
        <div>
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-9 w-40 mt-2" />
        </div>
        <SkeletonText lines={3} />
        <Skeleton className="h-11 w-64 max-w-full" />
      </div>
    </div>
    <span className="sr-only">Loading product details…</span>
  </div>
);

// ── Standard (non-assembly) product ──

const ProductImage = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);

  // No real image: a short tile, not a tall empty frame.
  if (!src || failed) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg h-40 lg:h-56 flex flex-col items-center justify-center">
        <ImageOff className="w-8 h-8 text-gray-300" aria-hidden="true" />
        <p className="text-sm text-gray-500 mt-2">No image available</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 h-56 lg:h-80 flex items-center justify-center">
      <img
        src={src}
        alt={alt}
        className="max-w-full max-h-full object-contain"
        onError={() => setFailed(true)}
      />
    </div>
  );
};

const stockBadge = (available, orderStatus) => {
  if (orderStatus) return { tone: 'info', label: orderStatus.trim(), Icon: Package };
  if (available <= 0) return { tone: 'danger', label: 'Out of Stock', Icon: AlertCircle };
  if (available <= 5) return { tone: 'warning', label: 'Low Stock', Icon: AlertTriangle };
  return { tone: 'success', label: 'In Stock', Icon: CheckCircle };
};

const StockSummary = ({ result, canViewFullStock, requestedQuantity, onCheckStauff }) => {
  const available = result.qty_available || 0;
  const unit = result.unit_code || '';
  const { tone, label, Icon } = stockBadge(available, result.order_status);
  const shortfall = requestedQuantity - available;

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-600">Availability</p>
        <StatusBadge tone={tone} icon={<Icon className="w-3 h-3" aria-hidden="true" />}>{label}</StatusBadge>
      </div>
      <p className="mt-1 text-gray-900">
        <span className="text-xl font-bold tabular-nums">{available}</span>
        <span className="text-sm text-gray-600"> {unit} available in Perth</span>
      </p>
      {canViewFullStock && (
        <p className="text-sm text-gray-600 mt-0.5 tabular-nums">
          {result.qty_on_hand ?? 0} on hand · {result.qty_allocated ?? 0} allocated
        </p>
      )}

      {shortfall > 0 ? (
        <div className="mt-3 p-3 rounded-lg border border-orange-200 bg-orange-50 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <div className="text-sm text-orange-900">
            <p className="font-semibold">
              {available > 0
                ? `Only ${available} of ${requestedQuantity} ${unit} available in Perth`
                : `None of the ${requestedQuantity} ${unit} requested is available in Perth`}
            </p>
            <p className="mt-0.5">
              {available > 0 ? `${shortfall} more ${unit} needed. ` : ''}
              <button
                type="button"
                onClick={onCheckStauff}
                className="font-medium underline underline-offset-2 hover:text-orange-950"
              >
                Check Stauff stock over east
              </button>
            </p>
          </div>
        </div>
      ) : requestedQuantity > 1 && (
        <p className="mt-3 text-sm text-green-800 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600" aria-hidden="true" />
          All {requestedQuantity} {unit} available in Perth
        </p>
      )}
    </div>
  );
};

const formatSpecLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

// Whole numbers stay whole (a thread size of 4 is not "4.00"); decimals are trimmed to 2 places.
const formatSpecValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number' && !Number.isInteger(value)) return String(parseFloat(value.toFixed(2)));
  return String(value);
};

const SpecRow = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-gray-100 text-sm">
    <dt className="text-gray-600">{label}</dt>
    <dd className="font-medium text-gray-900 text-right">{value}</dd>
  </div>
);

const Specifications = ({ result }) => {
  const specs = Object.entries(result.specifications || {})
    .filter(([key]) => key.toLowerCase() !== 'volume_cc');

  return (
    <Card header={<h2 className="text-base font-semibold text-gray-900">Specifications</h2>}>
      {result.article_description && (
        <p className="text-sm text-gray-700 leading-relaxed max-w-3xl mb-5">{result.article_description}</p>
      )}
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
        {result.prod_group && <SpecRow label="Product Group" value={result.prod_group} />}
        {result.unit_code && <SpecRow label="Unit" value={result.unit_code} />}
        {specs.map(([key, value]) => (
          <SpecRow key={key} label={formatSpecLabel(key)} value={formatSpecValue(value)} />
        ))}
      </dl>
    </Card>
  );
};

const ProductDetailView = ({
  result,
  cartQuantity,
  setCartQuantity,
  onAddToCart,
  isFavourite,
  onToggleFavourite,
  onOpenStauffModal,
  canViewFullStock,
  onAddToInquiry,
  inCartQty = 0,
  onViewCart,
}) => {
  const { enabled: orderingEnabled } = useOrderingEnabled();
  const price = parseFloat(result.buy_price);
  const hasPrice = price > 0;
  const unit = result.unit_code || '';
  const outOfStock = (result.qty_available || 0) === 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (orderingEnabled && onAddToCart) onAddToCart(result, cartQuantity);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <ProductHeader
          code={result.prod_code}
          description={result.Description}
          isFavourite={isFavourite}
          onToggleFavourite={onToggleFavourite}
        />

        <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Image sits below the buy box on mobile so price and Add to Cart stay on the first screen */}
          <div className="order-2 lg:order-1 lg:col-span-5">
            <ProductImage
              key={result.large_image_url}
              src={result.large_image_url}
              alt={[result.prod_code, result.Description].filter(Boolean).join(' - ')}
            />
          </div>

          {/* Buy box: price, availability, quantity, one primary action */}
          <div className="order-1 lg:order-2 lg:col-span-7 space-y-6">
            <PriceBlock price={result.buy_price} listPrice={result.base_price} unit={unit} />

            <div className="pt-6 border-t border-gray-200">
              <StockSummary
                result={result}
                canViewFullStock={canViewFullStock}
                requestedQuantity={cartQuantity}
                onCheckStauff={() => onOpenStauffModal(result.prod_code, true, cartQuantity)}
              />
            </div>

            <div className="pt-6 border-t border-gray-200 space-y-4">
              <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
                <QuantityInput id="product-quantity" value={cartQuantity} onChange={setCartQuantity} />
                {orderingEnabled && onAddToCart ? (
                  // No price → the inquiry list is the main route, so Add to Cart steps back.
                  <Button
                    type="submit"
                    size="lg"
                    variant={hasPrice ? 'primary' : 'secondary'}
                    className="font-semibold"
                    icon={<ShoppingCart className="w-4 h-4" aria-hidden="true" />}
                  >
                    Add to Cart
                  </Button>
                ) : (
                  <p className="text-sm font-medium text-gray-700 py-3">Online ordering is currently unavailable</p>
                )}
                {hasPrice && cartQuantity > 1 && (
                  <p className="text-sm text-gray-600 py-3 tabular-nums">
                    {cartQuantity} × {formatCurrency(price)} ={' '}
                    <span className="font-semibold text-gray-900">{formatCurrency(price * cartQuantity)}</span>
                  </p>
                )}
              </form>

              <InCartNote quantity={inCartQty} unit={unit} onViewCart={onViewCart} />

              <div className="flex flex-wrap gap-3">
                {onAddToInquiry && (
                  <Button
                    variant={hasPrice ? 'secondary' : 'primary'}
                    onClick={onAddToInquiry}
                    icon={<FileText className="w-4 h-4" aria-hidden="true" />}
                  >
                    Add to Inquiry
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() => onOpenStauffModal(result.prod_code, false, cartQuantity)}
                  icon={<Truck className="w-4 h-4" aria-hidden="true" />}
                >
                  Check Stauff Stock
                </Button>
                {outOfStock && !result.order_status && (
                  <NotifyMeButton partNumber={result.prod_code} isInStock={false} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Specifications result={result} />
    </div>
  );
};

export default ProductDetailView;
